import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { Plus, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

const TYPES = ['HeadOffice', 'DistrictOffice', 'Institute'];

const CostCenters = () => {
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', type: 'HeadOffice', fmas_district_id: '', fmas_institution_id: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchCostCenters = async () => {
    try {
      setLoading(true);
      const response = await accountsApi.get('/cost-centers');
      setCostCenters(response.data.cost_centers);
    } catch (err) {
      toast.error('Failed to load cost centers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCostCenters(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await accountsApi.post('/cost-centers', {
        code: form.code,
        name: form.name,
        type: form.type,
        fmas_district_id: form.fmas_district_id || null,
        fmas_institution_id: form.fmas_institution_id || null,
      });
      toast.success('Cost center created');
      setShowCreateModal(false);
      setForm({ code: '', name: '', type: 'HeadOffice', fmas_district_id: '', fmas_institution_id: '' });
      fetchCostCenters();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create cost center');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Cost Centers">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">Head Office, District Offices, and Institutes — the "where" behind every voucher.</p>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900"
        >
          <Plus size={16} /> New Cost Center
        </button>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Code</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Name</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Type</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">FMAS District</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">FMAS Institution</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : costCenters.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-500">No cost centers yet</td></tr>
            ) : costCenters.map(cc => (
              <tr key={cc.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-700">{cc.code}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{cc.name}</td>
                <td className="px-4 py-3 text-gray-600">{cc.type}</td>
                <td className="px-4 py-3 text-gray-600">{cc.fmas_district_id ?? '—'}</td>
                <td className="px-4 py-3 text-gray-600">{cc.fmas_institution_id ?? '—'}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${cc.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {cc.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2"><Building2 size={18} /> New Cost Center</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
                <input required type="text" value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. DO-3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input required type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">FMAS District ID (optional)</label>
                  <input type="number" value={form.fmas_district_id}
                    onChange={(e) => setForm({ ...form, fmas_district_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">FMAS Institution ID (optional)</label>
                  <input type="number" value={form.fmas_institution_id}
                    onChange={(e) => setForm({ ...form, fmas_institution_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <p className="text-xs text-gray-400">If given, these are validated live against FMAS — see the Districts/Institutions pages for their IDs.</p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CostCenters;
